import type {
	AgentRemoteEditReviewData,
	AgentRemoteEditReviewSegment,
} from '@atlaskit/editor-common/collab-agent-remote-edit-review';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Step } from '@atlaskit/editor-prosemirror/transform';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { getCollabState } from '@atlaskit/prosemirror-collab';

import { isPositionNeutralStep } from './agent-shimmer-ranges';

// [CCI-17994] Post Stream Review ("Review moment") recording for BE streaming.
//
// Sibling to `getAgentShimmerRanges`: same idea of deriving what the agent wrote
// from a received remote-step batch, but for a DIFFERENT consumer. The shimmer
// only needs the NEW extent of added content (to draw a skeleton), so it drops
// pure deletions and expands to whole blocks. The Review moment instead needs, per
// contiguous change, BOTH the original slice (pre-edit, for undo / "compare with
// original") and the new slice (for redo), AND it must keep deletions (a removed
// paragraph is a reviewable change). So this derives the actual changed extents
// and reconstructs the pre-edit slice from `tr.before`.
//
// The output is a neutral `AgentRemoteEditReviewData` (contract in editor-common);
// `editor-plugin-ai`'s session-context plugin turns each segment into a coarse
// `aiContentPositions` entry, reusing the entire FE Review moment pipeline.

const clampToDoc = (doc: PMNode, pos: number): number =>
	Math.min(Math.max(pos, 0), doc.content.size);

// Map a position in doc_{i+1} forward through the remaining steps to final-doc
// coords. Mirrors the shimmer's `mapToFinalDoc` — a later step's own inserted
// content starts at its `from`; only subsequent steps shift it.
const mapToFinalDoc = (steps: Step[], pos: number, stepIndex: number, bias: number): number => {
	let p = pos;
	for (let j = stepIndex + 1; j < steps.length; j++) {
		p = steps[j].getMap().map(p, bias);
	}
	return p;
};

/**
 * Derive per-change Review moment segments from an agent-authored remote-step
 * batch. Returns `null` when there is nothing to record (no agent steps, or a
 * rebase invalidated our index math, or derivation threw) so the caller can no-op
 * safely — this must never throw into the shared remote-step handler.
 *
 * @param json  the raw received step JSON (carries `agentType` / `agentId` / `userId`)
 * @param steps the parsed PM steps (index-aligned with `json`)
 * @param tr    the transaction that applied `steps` (so `tr.before`/`tr.doc`/`tr.mapping` are available)
 * @param view  the editor view (for the collab rebase guard)
 */
export const getAgentEditSegments = (
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	json: any[],
	steps: Step[],
	tr: Transaction,
	view: EditorView,
): AgentRemoteEditReviewData | null => {
	// `agentType` present ⇒ agent-authored (per the NCS↔Editor steps contract). Read
	// from the first agent step; it is identical across a batch.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const agentStep = json.find((step: any) => typeof step?.agentType === 'string');
	const agentType: string | undefined = agentStep?.agentType;
	if (agentType === undefined) {
		return null;
	}
	// The user the agent acted on behalf of (the requester). Optional/additive on the
	// step contract, so it may be absent.
	const actorUserId: string | undefined =
		typeof agentStep?.userId === 'string' ? agentStep.userId : undefined;
	// Which agent instance wrote the batch. Additive on the step contract like
	// `userId`, so treat it as optional. Carried for contract clarity / future
	// per-agent attribution; nothing consumes it yet.
	const agentId: string | undefined =
		typeof agentStep?.agentId === 'string' ? agentStep.agentId : undefined;
	// Hybrid end-of-edit seam: if the BE/NCS flags a batch as the terminal one, carry
	// it so the AI plugin can open review immediately instead of waiting for the
	// debounce. Additive marker; absent for streaming (non-final) batches.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const complete = json.some((step: any) => step?.agentEditComplete === true);

	// Same rebase guard as the shimmer: index-based range math is only valid if any
	// rebased-over local steps shifted no positions. Degrade to no recording otherwise.
	if (Number(tr.getMeta('rebased')) > 0) {
		const unconfirmed = getCollabState(view.state)?.unconfirmed ?? [];
		if (unconfirmed.some((entry) => !isPositionNeutralStep(entry.step))) {
			return null;
		}
	}

	try {
		// Compute one segment per step-level StepMap range. Each agent step's changed
		// extent is taken from its StepMap (the canonical, step-type-agnostic source).
		// Unlike the shimmer we KEEP zero-width new extents — a pure deletion has
		// `newEnd === newStart` but is a reviewable `remove`.
		//
		// We derive the originalSlice/newSlice PER RANGE (per step) rather than
		// grouping ranges first and then inverting over a merged range. Grouping
		// before slicing corrupts the invert-mapping: when multiple discrete
		// operations (e.g. two deleteNode + one insertNodeAfter) are merged into one
		// range, mapping that merged range back through the inverted mapping produces
		// a chimera — a blend of old and new text that never existed in the document.
		//
		// The downstream PSR pipeline (mergeOverlappingSegments +
		// calculateTopLevelNodeSegments) already handles overlapping/adjacent coarse
		// entries correctly, so grouping here is unnecessary.

		const segments: AgentRemoteEditReviewSegment[] = [];

		json.forEach((rawStep, index) => {
			if (typeof rawStep?.agentType !== 'string') {
				return;
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const pmStep = steps[index] as any;
			if (typeof pmStep?.getMap !== 'function') {
				return;
			}
			pmStep
				.getMap()
				.forEach((oldStart: number, oldEnd: number, newStart: number, newEnd: number) => {
					try {
						// NEW-doc coordinates: map this step's newStart/newEnd forward
						// through all subsequent steps to get final-doc positions.
						const finalFrom = mapToFinalDoc(steps, newStart, index, -1);
						const finalTo = mapToFinalDoc(steps, newEnd, index, 1);

						const newFrom = clampToDoc(tr.doc, Math.min(finalFrom, finalTo));
						const newTo = clampToDoc(tr.doc, Math.max(finalFrom, finalTo));

						// ORIGINAL-doc coordinates: map this step's oldStart/oldEnd
						// BACKWARD through all preceding steps to get pre-batch
						// (`tr.before`) positions. Each preceding step's StepMap tells
						// us how positions shifted; we invert that shift by mapping
						// through the inverted StepMap.
						//
						// This is the key difference from using `tr.mapping.invert()`
						// which inverts ALL steps at once — that produces chimera text
						// when multiple discrete operations (delete + delete + insert)
						// interact in the same region. Per-step backward mapping keeps
						// each operation's original range isolated.
						let origStart = oldStart;
						let origEnd = oldEnd;
						for (let j = index - 1; j >= 0; j--) {
							const prevMap = steps[j].getMap().invert();
							origStart = prevMap.map(origStart, -1);
							origEnd = prevMap.map(origEnd, 1);
						}
						const origFrom = clampToDoc(tr.before, Math.min(origStart, origEnd));
						const origTo = clampToDoc(tr.before, Math.max(origStart, origEnd));

						const originalSlice = tr.before.slice(origFrom, origTo);
						const newSlice = tr.doc.slice(newFrom, newTo);

						const originalEmpty = originalSlice.content.size === 0;
						const newEmpty = newSlice.content.size === 0;
						// A truly empty-to-empty region is not a change — skip it.
						if (originalEmpty && newEmpty) {
							return;
						}
						const kind: AgentRemoteEditReviewSegment['kind'] = originalEmpty
							? 'add'
							: newEmpty
								? 'remove'
								: 'update';

						segments.push({
							startPos: newFrom,
							endPos: newTo,
							originalSlice,
							newSlice,
							kind,
						});
					} catch {
						// One bad range must not drop the others.
					}
				});
		});

		if (!segments.length) {
			return null;
		}

		return { actorUserId, agentId, agentType, complete, segments };
	} catch {
		// Never throw into the shared remote-step handler; degrade to no recording.
		return null;
	}
};
